import React, { useRef, useState } from 'react'
import { useClickAway, useCopyToClipboard } from 'react-use'
import { Title, Text } from 'components/typo'
import DollarSign from 'components/shared/DollarSign'
import Button from 'components/shared/Button'
import Code from 'components/shared/Code'
import Tooltip from 'components/shared/Tooltip'
import CheckMark from 'components/icons/CheckMark'
import classnames from 'classnames/bind'
import css from './styles.module.scss'
import spreadStrapiData from 'src/lib/Strapi/spreadStrapiData'
import BuyGrowthModal from './buyGrowthModal'

const cx = classnames.bind(css)

const Feature = ({ name, id, tooltip, tagText }) => {
  return (
    <div key={name} className={css.feature}>
      {tooltip ? (
        <Tooltip position="bottom" content={tooltip}>
          <CheckMark className={css.checkMark} theme={'green'} />
          <Text
            className={cx(css.featureName, css.withTooltip)}
            key={id}
            size="small"
            theme="dark"
          >
            {name}
          </Text>
          {tagText && (
            <div className={cx(css.tag, css.tagPurple)}>{tagText}</div>
          )}
        </Tooltip>
      ) : (
        <>
          <CheckMark className={css.checkMark} theme={'green'} />
          <Text className={css.featureName} key={id} size="small" theme="dark">
            {name}
          </Text>
          {tagText && (
            <div className={cx(css.tag, css.tagPurple)}>{tagText}</div>
          )}
        </>
      )}
    </div>
  )
}

const GrowthPlanCustomFeatures = () => {
  return (
    <div>
      <hr className={css.divider} />
      <div className={css.buyGrowthModal__details}>
        <Text
          className={cx(
            css.customStarFeatureName,
            css.planTitle,
            css.titleMobile
          )}
          theme="dark"
        >
          Single Sign-On
          <div className={cx(css.tag, css.tagPurple)}>ADD-ON</div>
        </Text>
      </div>
      <div className={css.buyGrowthModal__details}>
        <Text theme="dark" className={css.ssoPrice}>
          $50{' '}
          <Text size="smaller" className={css.subtext}>
            &nbsp; per seat / month
          </Text>
        </Text>
      </div>
      <Text
        size="smaller"
        theme="neutral-600"
        className={cx(css.subtext_space, css.subtext)}
      >
        Authenticate with Okta, Microsoft EntraID, or multiple other SAML 2.0 or
        OAuth2 providers.
      </Text>
    </div>
  )
}

const PlanDetailsCard = ({
  className,
  featured,
  plan,
  starFeatures,
  mainFeatures,
  title,
  label,
  button,
  previousPlanName,
  highlighted,
  subtextComponent,
}) => {
  const [_, copyToClipboard] = useCopyToClipboard()
  const spreadedPlan = spreadStrapiData(plan)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const modalRef = useRef(null)

  let cardTheme =
    spreadedPlan && spreadedPlan.theme
      ? spreadedPlan.theme
      : button && button.theme
      ? button.theme
      : 'blue'

  cardTheme = spreadedPlan.name === 'Community' ? 'white' : spreadedPlan.theme

  const openModal = async () => {
    setIsModalOpen(true)
    document.body.style.overflow = 'hidden'
  }
  const closeModal = () => {
    setIsModalOpen(false)
    document.body.style.overflow = 'unset'
  }

  useClickAway(modalRef, closeModal)

  return (
    <div
      className={cx(
        className,
        highlighted ? css.PlanCardMostPopular : css.PlanCard,
        { featured }
      )}
    >
      {highlighted && (
        <div className={css.highlightedBorder}>
          <span className={css.highlightedLabel}>Best value</span>
        </div>
      )}
      {spreadedPlan
        ? spreadedPlan.name && (
            <Text
              className={cx(css.planTitle, css.titleMobile)}
              size="smaller"
              theme={cardTheme === 'white' ? 'default' : cardTheme}
            >
              {spreadedPlan.name}
            </Text>
          )
        : label && (
            <Text
              className={cx(css.planTitle, css.titleMobile)}
              size="small"
              theme={cardTheme}
            >
              {label}
            </Text>
          )}
      {spreadedPlan.type?.data?.attributes &&
        spreadedPlan.type?.data?.attributes?.name && (
          <div className={css.typeContainer}>
            <Text className={css.type} size="smaller">
              {spreadedPlan.type?.data?.attributes?.name}
              {spreadedPlan.type?.data?.attributes?.name === 'Cloud'
                ? ' managed'
                : ''}
            </Text>
            <Tooltip
              content={
                spreadedPlan.type?.data?.attributes?.name === 'Cloud'
                  ? 'We host Strapi for you'
                  : 'Host on your own server'
              }
              className={css.tooltip}
              position="right"
            />
          </div>
        )}
      {spreadedPlan ? (
        <>
          {spreadedPlan.price && (
            <Title tag="p" className={css.price} theme="dark" size="large">
              <DollarSign />
              <span>{spreadedPlan.price}</span>
            </Title>
          )}
          {spreadedPlan.text && (
            <Title tag="p" className={css.price} theme="dark" size="large">
              {spreadedPlan.text}
            </Title>
          )}
          {!subtextComponent && spreadedPlan.subtext && (
            <div className={css.subsection}>
              <Text className={css.subtext} size="small">
                {spreadedPlan.subtext}
              </Text>
            </div>
          )}

          {subtextComponent && (
            <div className={css.subsection}>{subtextComponent}</div>
          )}

          {spreadedPlan.commandLine && (
            <button
              className={css.code}
              onClick={() => copyToClipboard(spreadedPlan.commandLine)}
            >
              <Code
                className={css.codeBlock}
                theme="light"
                content={spreadedPlan.commandLine}
                noMargin
              />
            </button>
          )}
          {spreadedPlan.link && spreadedPlan.name !== 'Growth' && (
            <Button
              className={css.button}
              fullWidth
              theme={cardTheme}
              {...spreadedPlan.link}
              id={spreadedPlan?.name}
            />
          )}
          {spreadedPlan.name === 'Growth' && (
            <Button
              tagName="button"
              className={css.button}
              fullWidth
              theme={cardTheme}
              id={spreadedPlan?.name}
              onClick={(e) => {
                e.preventDefault()
                openModal()
              }}
            >
              Buy now
            </Button>
          )}
        </>
      ) : (
        <>
          {title && (
            <Title tag="p" className={cx(css.price, css.gray)} size="big">
              {title}
            </Title>
          )}
          {button && (
            <Button
              className={css.button}
              fullWidth
              theme={cardTheme}
              {...button}
            />
          )}
        </>
      )}
      {mainFeatures && mainFeatures.length > 0 && (
        <div className={css.features}>
          {mainFeatures.map(({ name, id, tooltip, tagText }) => (
            <Feature
              name={name}
              id={id}
              key={id}
              tooltip={tooltip}
              tagText={tagText}
            />
          ))}
        </div>
      )}
      <div className={css.features}>
        <Text size="small" className={css.featuresTitle} theme="dark">
          {previousPlanName
            ? `Everything in ${previousPlanName}, plus...`
            : `Plan features`}
        </Text>
        {starFeatures &&
          starFeatures.map(({ name, id, tooltip, tagText }) => (
            <Feature
              name={name}
              id={id}
              key={id}
              tooltip={tooltip}
              tagText={tagText}
            />
          ))}

        {spreadedPlan?.name === 'Growth' && <GrowthPlanCustomFeatures />}
      </div>
      <BuyGrowthModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        modalRef={modalRef}
        setIsModalOpen={setIsModalOpen}
        spreadedPlan={spreadedPlan}
        starFeatures={starFeatures}
      />
    </div>
  )
}

PlanDetailsCard.defaultProps = {}

export default PlanDetailsCard
